import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Form, Spinner } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function LeadMagnetContent() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState('generate'); // generate, generating, editing
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Content data
  const [contentOutline, setContentOutline] = useState('');
  const [savedContent, setSavedContent] = useState(null);
  const [savedTopic, setSavedTopic] = useState(null);
  const [targetMarket, setTargetMarket] = useState('');

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Get target market for personalized title
    try {
      const marketResponse = await fetch('/api/target-market/primary', {
        credentials: 'include'
      });

      if (marketResponse.ok) {
        const marketData = await marketResponse.json();
        if (marketData.primary_niche) {
          setTargetMarket(marketData.primary_niche.niche_name || '');
        }
      }
    } catch (err) {
      console.error('Error loading target market:', err);
    }

    // Check if user has saved topic (required)
    try {
      const topicResponse = await fetch('/api/assets/lead-magnet/get-topic', {
        credentials: 'include'
      });

      if (topicResponse.ok) {
        const topicData = await topicResponse.json();
        if (!topicData.topic) {
          // No topic saved, redirect to topic page
          navigate('/lead-magnet-topic');
          return;
        }
        setSavedTopic(topicData.topic);
      } else {
        navigate('/lead-magnet-topic');
        return;
      }

      // Check if user has saved content
      const contentResponse = await fetch('/api/assets/lead-magnet/get-content', {
        credentials: 'include'
      });

      if (contentResponse.ok) {
        const contentData = await contentResponse.json();
        if (contentData.content) {
          setSavedContent(contentData.content);
          setContentOutline(contentData.content);
          setStage('editing');
        }
      }
    } catch (err) {
      console.error('Error loading saved data:', err);
    }

    setLoading(false);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setStage('generating');

    try {
      const response = await fetch('/api/assets/lead-magnet/generate-content', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setContentOutline(data.content);
        setStage('editing');
      } else {
        const errorData = await response.json();
        setError(`Failed to generate content: ${errorData.error || 'Unknown error'}`);
        setStage('generate');
      }
    } catch (err) {
      setError('Error generating content: ' + err.message);
      setStage('generate');
    }

    setGenerating(false);
  };

  const handleSave = async () => {
    if (!contentOutline.trim()) {
      setError('Please enter your content draft');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/assets/lead-magnet/save-content', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: contentOutline })
      });

      if (response.ok) {
        setSavedContent(contentOutline);
        // Show success message
        setError('');
        alert('Content draft saved successfully!');
      } else {
        const errorData = await response.json();
        setError(`Failed to save content: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      setError('Error saving content: ' + err.message);
    }

    setSaving(false);
  };

  const handleContinue = () => {
    navigate('/lead-magnet-landing');
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await fetch('/api/assets/lead-magnet/download-pdf', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        // Get the filename from the Content-Disposition header if available
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'lead-magnet.pdf';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        // Convert response to blob and download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const errorData = await response.json();
        setError(`Failed to download PDF: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      setError('Error downloading PDF: ' + err.message);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="assets-creation" />
      <Row className="mb-4">
        <Col>
          <h1>Lead Magnet Content Draft{targetMarket && ` for ${targetMarket}`}</h1>
          <p className="text-muted">
            Create a complete, ready-to-use draft for your lead magnet content.
          </p>
          {savedTopic && (
            <Alert variant="info">
              <strong>Lead Magnet Topic:</strong> {savedTopic.topic}
            </Alert>
          )}
        </Col>
      </Row>

      {error && !error.includes('saved successfully') && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {/* Generate Stage */}
      {stage === 'generate' && (
        <Row>
          <Col md={8}>
            <Card>
              <Card.Body>
                <h4 className="mb-4">📝 Generate Content Draft</h4>
                <p className="mb-4">
                  Our AI will create a complete, ready-to-use content draft based on your lead magnet topic, target audience, and business context.
                </p>

                <Alert variant="info" className="mb-4">
                  <strong>What you'll get:</strong>
                  <ul className="mb-0 mt-2">
                    <li>Complete introduction with compelling hook</li>
                    <li>3-5 fully-written content sections with actionable information</li>
                    <li>Specific examples, takeaways, and exercises</li>
                    <li>Conclusion with natural bridge to your core product</li>
                    <li>Suggestions for visuals, graphics, and bonus resources</li>
                  </ul>
                </Alert>

                <div className="d-grid">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleGenerate}
                    disabled={generating}
                  >
                    {generating ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Generating Content Draft... (45-60 seconds)
                      </>
                    ) : (
                      'Generate Content Draft →'
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="bg-light">
              <Card.Body>
                <h5 className="mb-3">💡 Draft Tips</h5>
                <ul className="small mb-0">
                  <li>Review for your brand voice and tone</li>
                  <li>Add personal stories or examples</li>
                  <li>Adjust complexity for your audience</li>
                  <li>Verify all facts and statistics</li>
                  <li>Add visual markers (headings, bullets)</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Generating Stage */}
      {stage === 'generating' && (
        <Row>
          <Col md={12}>
            <Card>
              <Card.Body className="text-center py-5">
                <Spinner animation="border" className="mb-3" />
                <h4>Generating Your Content Draft...</h4>
                <p className="text-muted">
                  This will take about 45-60 seconds. We're analyzing your business context and writing a complete, ready-to-use draft.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Editing Stage */}
      {stage === 'editing' && (
        <>
          <Row className="mb-4">
            <Col md={12}>
              <Card>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">✏️ Review & Edit Content Draft</h4>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleGenerate}
                      disabled={generating}
                    >
                      {generating ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Regenerating...
                        </>
                      ) : (
                        'Regenerate Draft'
                      )}
                    </Button>
                  </div>

                  <Alert variant="info" className="mb-4">
                    <strong>Review the AI-generated draft below.</strong> You can edit it directly or regenerate a new version.
                  </Alert>

                  <Form.Group className="mb-4">
                    <Form.Label><strong>Content Draft:</strong></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={30}
                      value={contentOutline}
                      onChange={(e) => setContentOutline(e.target.value)}
                      style={{ fontFamily: 'monospace', fontSize: '14px' }}
                    />
                    <Form.Text className="text-muted">
                      This is your complete lead magnet content. Edit to match your brand voice and add personal touches.
                    </Form.Text>
                  </Form.Group>

                  <div className="d-grid gap-2">
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleSave}
                      disabled={saving || !contentOutline.trim()}
                    >
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        'Save Content Draft'
                      )}
                    </Button>

                    {savedContent && (
                      <>
                        <Button
                          variant="success"
                          size="lg"
                          onClick={handleDownloadPDF}
                        >
                          📄 Download as PDF
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="lg"
                          onClick={handleContinue}
                        >
                          Continue to Landing Page Copy →
                        </Button>
                      </>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {savedContent && (
            <Row>
              <Col md={12}>
                <Alert variant="success">
                  <strong>✓ Content Draft Saved</strong><br />
                  You can continue to create your landing page copy, or make additional edits here.
                </Alert>
              </Col>
            </Row>
          )}
        </>
      )}
    </Container>
  );
}

export default LeadMagnetContent;
